import '../styles/GlobalDesign.css';
import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

const initialForm = {
  app_name: 'Shamel TV',
  custom_app_name: '',
  device_id: '',
  device_key: '',
  user_id: '',
  xtream_account_id: '',
  expires_at: '',
  notes: ''
};

function dateTimeLocalPlusDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR');
}

function isExpired(value) {
  return value ? new Date(value) < new Date() : false;
}

function normalizeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function matchesSearch(values, query) {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return true;
  return values.some(value => normalizeSearch(value).includes(normalizedQuery));
}

function AdminAppActivations() {
  const [activations, setActivations] = useState([]);
  const [users, setUsers] = useState([]);
  const [xtreamAccounts, setXtreamAccounts] = useState([]);
  const [appPresets, setAppPresets] = useState(['Shamel TV', 'IBO Player', 'Smart IPTV', 'Room IPTV', 'Autre']);
  const [userSearch, setUserSearch] = useState('');
  const [xtreamSearch, setXtreamSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [form, setForm] = useState(() => ({ ...initialForm, expires_at: dateTimeLocalPlusDays(365) }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const authHeaders = (json = false) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
  };

  const loadData = async () => {
    setError('');
    try {
      const [listRes, optionsRes] = await Promise.all([
        fetch(`${API_URL}/app-activations`, { headers: authHeaders() }),
        fetch(`${API_URL}/app-activations/options`, { headers: authHeaders() })
      ]);

      if (listRes.status === 401 || listRes.status === 403) {
        window.location.href = '/admin/login';
        return;
      }
      if (!listRes.ok) throw new Error('Impossible de charger les activations apps');
      if (!optionsRes.ok) throw new Error('Impossible de charger les options');

      const listData = await listRes.json();
      const optionsData = await optionsRes.json();
      setActivations(listData.activations || []);
      setUsers(optionsData.users || []);
      setXtreamAccounts(optionsData.xtreamAccounts || []);
      setAppPresets(optionsData.appPresets || appPresets);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedXtreamAccount = useMemo(() => (
    xtreamAccounts.find(acc => String(acc.id) === String(form.xtream_account_id))
  ), [form.xtream_account_id, xtreamAccounts]);

  const filteredUsers = useMemo(() => (
    users.filter(user => matchesSearch([user.nom, user.email], userSearch))
  ), [users, userSearch]);

  const filteredXtreamAccounts = useMemo(() => {
    const query = normalizeSearch(xtreamSearch);
    const source = form.user_id && !query
      ? xtreamAccounts.filter(acc => String(acc.user_id) === String(form.user_id))
      : xtreamAccounts;

    return source.filter(acc => matchesSearch([
      acc.username,
      acc.nom,
      acc.email,
      acc.reseller_name,
      acc.is_trial === 1 ? 'test demo trial essai' : '',
      acc.is_enabled === 0 ? 'desactive suspendu disabled' : '',
      acc.is_expired === 1 ? 'expire expired' : ''
    ], query));
  }, [form.user_id, xtreamAccounts, xtreamSearch]);

  const filteredActivations = useMemo(() => (
    activations.filter(item => matchesSearch([
      item.app_name,
      item.device_id,
      item.device_key,
      item.xtream_username,
      item.nom,
      item.email,
      item.reseller_name,
      item.notes
    ], tableSearch))
  ), [activations, tableSearch]);

  const stats = useMemo(() => {
    const active = activations.filter(item => item.is_enabled === 1 && !isExpired(item.expires_at)).length;
    const expired = activations.filter(item => isExpired(item.expires_at)).length;
    const disabled = activations.filter(item => item.is_enabled !== 1).length;
    return { total: activations.length, active, expired, disabled };
  }, [activations]);

  const updateForm = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'user_id' && selectedXtreamAccount && String(selectedXtreamAccount.user_id) !== String(value)
        ? { xtream_account_id: '' }
        : {})
    }));
  };

  const updateXtreamAccount = (value) => {
    const account = xtreamAccounts.find(acc => String(acc.id) === String(value));
    setForm(prev => ({
      ...prev,
      xtream_account_id: value,
      ...(account?.user_id ? { user_id: String(account.user_id) } : {})
    }));
  };

  const effectiveAppName = () => (
    form.app_name === 'Autre' ? form.custom_app_name : form.app_name
  );

  const formatXtreamOption = (account) => {
    const tags = [
      account.is_trial === 1 ? 'TEST' : '',
      account.is_enabled === 0 ? 'OFF' : '',
      account.is_expired === 1 ? 'EXPIRÉ' : '',
      account.reseller_name ? `Revendeur: ${account.reseller_name}` : ''
    ].filter(Boolean);

    return [
      account.username,
      tags.length ? `[${tags.join(' / ')}]` : '',
      account.nom || account.email || '',
      `expire ${formatDate(account.expires_at)}`
    ].filter(Boolean).join(' - ');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/app-activations`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          app_name: effectiveAppName(),
          device_id: form.device_id,
          device_key: form.device_key,
          user_id: form.user_id,
          xtream_account_id: form.xtream_account_id,
          expires_at: new Date(form.expires_at).toISOString(),
          notes: form.notes
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec création activation app');

      setForm({ ...initialForm, expires_at: dateTimeLocalPlusDays(365) });
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyActivation = async (activation) => {
    const config = activation.config || {};
    const text = [
      'Activation application Fluxivo',
      `Application: ${activation.app_name}`,
      `MAC / Device ID: ${activation.device_id}`,
      `Clé appareil: ${activation.device_key || '-'}`,
      `Client: ${activation.nom || activation.email || '-'}`,
      '',
      'Configuration à entrer dans le panel de l’application:',
      `M3U: ${config.m3u_url || '-'}`,
      `Xtream Server: ${config.server_url || '-'}`,
      `Username: ${config.username || '-'}`,
      `Password: ${config.password || '-'}`,
      '',
      `Expire: ${formatDate(activation.expires_at)}`
    ].join('\n');

    await navigator.clipboard.writeText(text);
    setCopiedId(activation.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const editDeviceKey = async (activation) => {
    const value = window.prompt(`Clé appareil pour ${activation.app_name} (${activation.device_id})`, activation.device_key || '');
    if (value === null) return;

    try {
      const response = await fetch(`${API_URL}/app-activations/${activation.id}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ device_key: value })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec modification clé');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggle = async (activation) => {
    try {
      const response = await fetch(`${API_URL}/app-activations/${activation.id}/toggle`, {
        method: 'PUT',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec modification');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (activation) => {
    if (!window.confirm(`Supprimer l'activation ${activation.app_name} pour ${activation.device_id} ?`)) return;

    try {
      const response = await fetch(`${API_URL}/app-activations/${activation.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec suppression');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>Activation Apps</h1>
          <p style={styles.subtitle}>Shamel TV, IBO Player, Smart IPTV et applications à clé appareil</p>
        </div>
        <button className="btn-back" onClick={() => window.location.href = '/admin/home'}>Retour</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div style={styles.statsGrid}>
        <div style={styles.stat}><strong>{stats.total}</strong><span>Total</span></div>
        <div style={styles.stat}><strong>{stats.active}</strong><span>Actives</span></div>
        <div style={styles.stat}><strong>{stats.expired}</strong><span>Expirées</span></div>
        <div style={styles.stat}><strong>{stats.disabled}</strong><span>Désactivées</span></div>
      </div>

      <div style={styles.infoBox}>
        <strong>Fluxivo prépare les accès à copier dans le panel de l’application.</strong>
        <span>Ces apps ne contactent pas directement Fluxivo avec une Portal URL : on stocke leur MAC / Device ID et leur clé, puis on fournit le M3U ou les identifiants Xtream.</span>
      </div>

      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.formHeader}>
          <h2>Nouvelle activation app</h2>
          <div style={styles.presets}>
            {[7, 30, 90, 365].map(days => (
              <button key={days} type="button" style={styles.presetButton} onClick={() => updateForm('expires_at', dateTimeLocalPlusDays(days))}>
                {days === 365 ? '1 an' : `${days}j`}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.formGrid}>
          <label style={styles.field}>
            <span>Application</span>
            <select value={form.app_name} onChange={(e) => updateForm('app_name', e.target.value)} required>
              {appPresets.map(app => <option key={app} value={app}>{app}</option>)}
            </select>
          </label>

          {form.app_name === 'Autre' && (
            <label style={styles.field}>
              <span>Nom application</span>
              <input value={form.custom_app_name} onChange={(e) => updateForm('custom_app_name', e.target.value)} placeholder="Nom de l'application" required />
            </label>
          )}

          <label style={styles.field}>
            <span>MAC / Device ID</span>
            <input value={form.device_id} onChange={(e) => updateForm('device_id', e.target.value)} placeholder="Ex: A0:54:BE:20:E8:D2" required />
          </label>

          <label style={styles.field}>
            <span>Clé appareil</span>
            <input value={form.device_key} onChange={(e) => updateForm('device_key', e.target.value)} placeholder="Clé fournie par l'application" />
          </label>

          <label style={styles.field}>
            <span>Client</span>
            <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Filtrer par nom ou email" style={styles.searchInput} />
            <select value={form.user_id} onChange={(e) => updateForm('user_id', e.target.value)} required>
              <option value="">Choisir un client</option>
              {filteredUsers.map(user => (
                <option key={user.id} value={user.id}>{user.nom || user.email} - {user.email}</option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span>Compte Xtream lié</span>
            <input value={xtreamSearch} onChange={(e) => setXtreamSearch(e.target.value)} placeholder="Filtrer username, client, revendeur" style={styles.searchInput} />
            <select value={form.xtream_account_id} onChange={(e) => updateXtreamAccount(e.target.value)} required>
              <option value="">Choisir un compte Xtream</option>
              {filteredXtreamAccounts.map(account => (
                <option key={account.id} value={account.id}>{formatXtreamOption(account)}</option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span>Expiration</span>
            <input type="datetime-local" value={form.expires_at} onChange={(e) => updateForm('expires_at', e.target.value)} required />
          </label>

          <label style={styles.field}>
            <span>Notes</span>
            <input value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} placeholder="Optionnel" />
          </label>
        </div>

        <button style={styles.primaryButton} disabled={saving}>
          {saving ? 'Création...' : 'Créer activation app'}
        </button>
      </form>

      <div style={styles.listHeader}>
        <h2>Activations apps</h2>
        <div style={styles.listTools}>
          <input value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} placeholder="Filtrer la liste" style={styles.listSearch} />
          <button style={styles.smallButton} onClick={loadData}>Rafraîchir</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.empty}>Chargement...</div>
      ) : filteredActivations.length === 0 ? (
        <div style={styles.empty}>Aucune activation app trouvée.</div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Application</th>
                <th>MAC / Device ID</th>
                <th>Clé</th>
                <th>Client</th>
                <th>Xtream</th>
                <th>Expiration</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivations.map(activation => {
                const expired = isExpired(activation.expires_at);
                const active = activation.is_enabled === 1 && !expired;
                return (
                  <tr key={activation.id}>
                    <td><strong>{activation.app_name}</strong></td>
                    <td style={styles.mono}>{activation.device_id}</td>
                    <td style={styles.mono}>{activation.device_key || '-'}</td>
                    <td>
                      <strong>{activation.nom || '-'}</strong>
                      <div style={styles.muted}>{activation.email || ''}</div>
                    </td>
                    <td style={styles.mono}>{activation.xtream_username}</td>
                    <td>{formatDate(activation.expires_at)}</td>
                    <td>
                      <span style={{ ...styles.badge, ...(active ? styles.badgeActive : expired ? styles.badgeExpired : styles.badgeDisabled) }}>
                        {active ? 'Actif' : expired ? 'Expiré' : 'Désactivé'}
                      </span>
                    </td>
                    <td>
                      <div style={styles.actions}>
                        <button style={styles.actionButton} onClick={() => copyActivation(activation)}>{copiedId === activation.id ? 'Copié' : 'Copier'}</button>
                        <button style={styles.actionButton} onClick={() => editDeviceKey(activation)}>Modifier clé</button>
                        <button style={styles.actionButton} onClick={() => handleToggle(activation)}>{activation.is_enabled === 1 ? 'Suspendre' : 'Activer'}</button>
                        <button style={styles.dangerButton} onClick={() => handleDelete(activation)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

const styles = {
  subtitle: { color: '#94A3B8', marginTop: 4 },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 16
  },
  stat: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  infoBox: {
    background: '#0F172A',
    border: '1px solid rgba(34, 197, 94, 0.35)',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    color: '#CBD5E1',
    marginBottom: 16
  },
  form: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8,
    padding: 18,
    marginBottom: 20
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap'
  },
  presets: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  presetButton: {
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background: '#1F2937',
    color: '#E5E7EB',
    borderRadius: 6,
    padding: '8px 10px',
    cursor: 'pointer'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: 14
  },
  field: { display: 'flex', flexDirection: 'column', gap: 6, color: '#CBD5E1', fontSize: 13 },
  searchInput: { marginBottom: 4 },
  primaryButton: {
    marginTop: 16,
    background: '#16A34A',
    color: 'white',
    border: 0,
    borderRadius: 8,
    padding: '12px 16px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  smallButton: {
    background: '#1F2937',
    color: '#E5E7EB',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 6,
    padding: '9px 12px',
    cursor: 'pointer'
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap'
  },
  listTools: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  listSearch: { minWidth: 220 },
  empty: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8,
    padding: 24,
    color: '#94A3B8',
    textAlign: 'center'
  },
  tableWrap: {
    overflowX: 'auto',
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 1100 },
  mono: { fontFamily: 'monospace', color: '#A7F3D0' },
  muted: { color: '#94A3B8', fontSize: 12, marginTop: 3 },
  badge: { display: 'inline-flex', padding: '5px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 },
  badgeActive: { color: '#86EFAC', background: 'rgba(22, 163, 74, 0.14)' },
  badgeExpired: { color: '#FCA5A5', background: 'rgba(239, 68, 68, 0.14)' },
  badgeDisabled: { color: '#CBD5E1', background: 'rgba(148, 163, 184, 0.14)' },
  actions: { display: 'flex', gap: 7, flexWrap: 'wrap' },
  actionButton: {
    background: '#1F2937',
    color: '#E5E7EB',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 6,
    padding: '7px 9px',
    cursor: 'pointer'
  },
  dangerButton: {
    background: 'rgba(239, 68, 68, 0.12)',
    color: '#FCA5A5',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 6,
    padding: '7px 9px',
    cursor: 'pointer'
  }
};

export default AdminAppActivations;
